import React, { useState, useEffect } from "react";
import Navbar from "./components/Navbar";
import UpdatesTicker from "./components/UpdatesTicker";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Tracking from "./pages/Tracking";
import Support from "./pages/Support";
import Dashboard from "./pages/Dashboard";
import AdminOps from "./pages/AdminOps";

// Import newly created modular services, solutions, partner, and company views
import ExpressParcel from "./pages/services/ExpressParcel";
import Warehousing from "./pages/services/Warehousing";
import PartTruckload from "./pages/services/PartTruckload";
import FullTruckload from "./pages/services/FullTruckload";
import International from "./pages/services/International";
import LocalDelivery from "./pages/services/LocalDelivery";
import TransportOne from "./pages/services/TransportOne";
import DataIntelligence from "./pages/services/DataIntelligence";
import JpsyMaps from "./pages/services/JpsyMaps";
import D2cBrands from "./pages/solutions/D2cBrands";
import PersonalCourier from "./pages/solutions/PersonalCourier";
import B2bEnterprises from "./pages/solutions/B2bEnterprises";
import RtoPredictor from "./pages/solutions/RtoPredictor";
import FranchiseOpportunities from "./pages/partner/FranchiseOpportunities";
import LogisticsSchool from "./pages/partner/LogisticsSchool";
import DeliveryPartner from "./pages/partner/DeliveryPartner";
import FleetOwners from "./pages/partner/FleetOwners";
import AboutUs from "./pages/company/AboutUs";
import Governance from "./pages/company/Governance";
import PressReleases from "./pages/company/PressReleases";
import Careers from "./pages/company/Careers";
import Blog from "./pages/company/Blog";
import GenericPage from "./pages/GenericPage";

import { INITIAL_SHIPMENTS } from "./data/shipmentData";

export default function App() {
  const [currentTab, setCurrentTab] = useState("home");
  const [shipments, setShipments] = useState(() => {
    // Attempt load from localStorage for premium persistence feel
    const cached = localStorage.getItem("jpsy_shipments");
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        return INITIAL_SHIPMENTS;
      }
    }
    return INITIAL_SHIPMENTS;
  });

  const [trackedShipment, setTrackedShipment] = useState(null);

  useEffect(() => {
    document.title =
      currentTab === "tracking"
        ? "JPSY Tracking | Track Parcel & Shipment Delivery"
        : "JPSY Supply Chain";
  }, [currentTab]);

  // Sync to localStorage
  useEffect(() => {
    localStorage.setItem("jpsy_shipments", JSON.stringify(shipments));
  }, [shipments]);

  // Keep active tracked shipment updated if it changes in parent state
  useEffect(() => {
    if (trackedShipment) {
      const match = shipments.find((s) => s.awb === trackedShipment.awb);
      if (match) {
        setTrackedShipment(match);
      }
    }
  }, [shipments, trackedShipment?.awb]);

  // Listen to deep linking events
  useEffect(() => {
    const handleTrackItem = (e) => {
      const customEvent = e;
      if (customEvent.detail?.shipment) {
        setTrackedShipment(customEvent.detail.shipment);
      }
    };

    const handleNavigate = (e) => {
      const customEvent = e;
      if (customEvent.detail?.tab) {
        setCurrentTab(customEvent.detail.tab);
      }
    };

    window.addEventListener("jpsy-track-item", handleTrackItem);
    window.addEventListener("jpsy-navigate", handleNavigate);

    return () => {
      window.removeEventListener("jpsy-track-item", handleTrackItem);
      window.removeEventListener("jpsy-navigate", handleNavigate);
    };
  }, []);

  const handleAddShipment = (newShipment) => {
    setShipments((prev) => [newShipment, ...prev]);
  };

  const handleUpdateShipment = (updatedShipment) => {
    setShipments((prev) =>
      prev.map((s) => (s.awb === updatedShipment.awb ? updatedShipment : s)),
    );
  };

  const handleResetData = () => {
    if (
      window.confirm(
        "Reset sandbox shipments and transactions back to initial factory setup?",
      )
    ) {
      setShipments(INITIAL_SHIPMENTS);
      setTrackedShipment(null);
      localStorage.removeItem("jpsy_shipments");
    }
  };

  // List of tabs that are mapped to GenericPage
  const genericTabs = [







    "investors",

  ];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-800 antialiased selection:bg-[#263f98] selection:text-white">
      {/* Top sticky Navbar */}
      <Navbar currentTab={currentTab} onTabChange={setCurrentTab} />
      {currentTab === "tracking" && <UpdatesTicker />}

      {/* Main viewport transition wrapper */}
      <main className="flex-grow flex flex-col">
        {currentTab === "tracking" && (<Tracking shipments={shipments} onTabChange={setCurrentTab} />)}
        {currentTab === "support" && (<Support onTabChange={setCurrentTab} />)}
        {currentTab === "home" && (
          <Home
            shipments={shipments}
            trackedShipment={trackedShipment}
            setTrackedShipment={setTrackedShipment}
            onTabChange={setCurrentTab}
          />
        )}

        {currentTab === "dashboard" && (
          <Dashboard
            shipments={shipments}
            onAddShipment={handleAddShipment}
            onTabChange={setCurrentTab}
          />
        )}

        {currentTab === "admin" && (
          <AdminOps
            shipments={shipments}
            onUpdateShipment={handleUpdateShipment}
            onResetData={handleResetData}
          />
        )}

        {/* Modular Service Sub-pages */}
        {currentTab === "service_express_parcel" && (
          <ExpressParcel onTabChange={setCurrentTab} />
        )}
        {currentTab === "service_warehousing" && (
          <Warehousing onTabChange={setCurrentTab} />
        )}
        {currentTab === "service_part_truckload" && (
          <PartTruckload onTabChange={setCurrentTab} />
        )}
        {currentTab === "service_full_truckload" && (
          <FullTruckload onTabChange={setCurrentTab} />
        )}
        {currentTab === "service_local_delivery" && (<LocalDelivery onTabChange={setCurrentTab} />)}
        {currentTab === "service_international" && (
          <International onTabChange={setCurrentTab} />
        )}
        {currentTab === "service_transport_one" && (
          <TransportOne onTabChange={setCurrentTab} />
        )}
        {currentTab === "service_data_intelligence" && (
          <DataIntelligence onTabChange={setCurrentTab} />
        )}
        {currentTab === "service_jpsy_maps" && (
          <JpsyMaps onTabChange={setCurrentTab} />
        )}

        {/* Modular Solution Sub-pages */}
        {currentTab === "solution_d2c_brands" && (
          <D2cBrands onTabChange={setCurrentTab} />
        )}
        {currentTab === "solution_b2b_enterprises" && (<B2bEnterprises onTabChange={setCurrentTab} />)}
        {currentTab === "solution_rto_predictor" && (<RtoPredictor onTabChange={setCurrentTab} />)}
        {currentTab === "solution_personal_courier" && (
          <PersonalCourier onTabChange={setCurrentTab} />
        )}

        {/* Modular Partner Sub-pages */}
        {currentTab === "partner_logistics_school" && (<LogisticsSchool onTabChange={setCurrentTab} />)}
        {currentTab === "partner_delivery_partner" && (<DeliveryPartner onTabChange={setCurrentTab} />)}
        {currentTab === "partner_fleet_owners" && (<FleetOwners onTabChange={setCurrentTab} />)}
        {currentTab === "partner_franchise" && (
          <FranchiseOpportunities onTabChange={setCurrentTab} />
        )}

        {/* Modular Company Sub-pages */}
        {currentTab === "company_governance" && (<Governance onTabChange={setCurrentTab} />)}
        {currentTab === "company_press_release" && (<PressReleases onTabChange={setCurrentTab} />)}
        {currentTab === "company_careers" && (<Careers onTabChange={setCurrentTab} />)}
        {currentTab === "company_blog" && (<Blog onTabChange={setCurrentTab} />)}
        {currentTab === "company_about_us" && (
          <AboutUs onTabChange={setCurrentTab} />
        )}

        {/* Generic/Dynamic Information pages */}
        {genericTabs.includes(currentTab) && (
          <GenericPage tab={currentTab} onTabChange={setCurrentTab} />
        )}
      </main>

      {/* Corporate footer info */}
      <Footer onTabChange={setCurrentTab} />
    </div>
  );
}







